/* eslint-disable no-undef */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { BrandPendulumMark } from '../BrandPendulumMark';

type PendingPlanAction = 'approve' | 'deny' | null;

interface DevPlanExplainModalProps {
  /** Self-contained HTML presentation produced by the backend.
   *  Mermaid blocks have already been rendered to inline SVG; the doc carries
   *  its own slide navigation (`window.showSlide(i)`) and posts
   *  `{type:"8080-presentation-ready", count}` on load. */
  presentationHtml: string;
  onClose: () => void;
  showPlanActions?: boolean;
  pending?: PendingPlanAction;
  error?: string | null;
  onApprove?: () => void;
  onDeny?: () => void;
}

interface ReadyEvent {
  type: '8080-presentation-ready';
  count: number;
}

interface ChangedEvent {
  type: '8080-slide-changed';
  index: number;
}

interface ViewState {
  scale: number;
  x: number;
  y: number;
}

interface Point {
  x: number;
  y: number;
}

type GestureLikeEvent = Event & {
  clientX?: number;
  clientY?: number;
  scale?: number;
};

const INITIAL_VIEW: ViewState = { scale: 1, x: 0, y: 0 };

const clampZoom = (scale: number) => Math.max(0.55, Math.min(2.75, scale));

const pointDistance = (points: Point[]) => (
  points.length < 2 ? 0 : Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)
);

const pointCenter = (points: Point[]): Point => ({
  x: (points[0].x + points[1].x) / 2,
  y: (points[0].y + points[1].y) / 2,
});

const isReadyEvent = (data: unknown): data is ReadyEvent => (
  !!data && typeof data === 'object'
    && (data as { type?: unknown }).type === '8080-presentation-ready'
    && typeof (data as { count?: unknown }).count === 'number'
);

const isChangedEvent = (data: unknown): data is ChangedEvent => (
  !!data && typeof data === 'object'
    && (data as { type?: unknown }).type === '8080-slide-changed'
    && typeof (data as { index?: unknown }).index === 'number'
);

const ApproveIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="m18 6-12 12M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const Spinner: React.FC = () => <span className="dev-plan-btn__spinner" aria-hidden="true" />;

// eslint-disable-next-line max-statements, max-lines-per-function
export const DevPlanExplainModal: React.FC<DevPlanExplainModalProps> = ({
  presentationHtml,
  onClose,
  showPlanActions = false,
  pending = null,
  error = null,
  onApprove,
  onDeny,
}) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<ViewState>(INITIAL_VIEW);
  const pointerSession = useRef({
    pointerId: undefined as number | undefined,
    pointers: new Map<number, Point>(),
    pinchStartDistance: 0,
    pinchStartScale: 1,
    pinchStartX: 0,
    pinchStartY: 0,
    startPanX: 0,
    startPanY: 0,
    startX: 0,
    startY: 0,
  });
  const [iframeEl, setIframeEl] = useState<HTMLIFrameElement | null>(null);
  const [count, setCount] = useState(0);
  const [index, setIndex] = useState(0);
  const [view, setView] = useState<ViewState>(INITIAL_VIEW);
  const [isPanning, setIsPanning] = useState(false);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  const clampView = useCallback((next: ViewState): ViewState => {
    const rect = bodyRef.current?.getBoundingClientRect();
    const scale = clampZoom(next.scale);
    if (!rect) return { ...next, scale };

    const scaledWidth = rect.width * scale;
    const scaledHeight = rect.height * scale;
    let x = next.x;
    let y = next.y;

    x = scaledWidth <= rect.width
      ? (rect.width - scaledWidth) / 2
      : Math.min(0, Math.max(rect.width - scaledWidth, x));
    y = scaledHeight <= rect.height
      ? (rect.height - scaledHeight) / 2
      : Math.min(0, Math.max(rect.height - scaledHeight, y));

    return { scale, x, y };
  }, []);

  const updateView = useCallback((next: ViewState) => {
    const clamped = clampView(next);
    viewRef.current = clamped;
    setView(clamped);
  }, [clampView]);

  const resetZoom = useCallback(() => {
    const session = pointerSession.current;
    session.pointerId = undefined;
    session.pinchStartDistance = 0;
    session.pointers.clear();
    viewRef.current = INITIAL_VIEW;
    setIsPanning(false);
    setView(INITIAL_VIEW);
  }, []);

  const zoomBy = useCallback((factor: number, originX?: number, originY?: number) => {
    const rect = bodyRef.current?.getBoundingClientRect();
    const current = viewRef.current;
    const nextScale = clampZoom(current.scale * factor);
    if (!rect) {
      updateView({ ...current, scale: nextScale });
      return;
    }

    const pointX = originX ?? rect.left + rect.width / 2;
    const pointY = originY ?? rect.top + rect.height / 2;
    const localX = pointX - rect.left;
    const localY = pointY - rect.top;
    const worldX = (localX - current.x) / current.scale;
    const worldY = (localY - current.y) / current.scale;

    updateView({
      scale: nextScale,
      x: localX - worldX * nextScale,
      y: localY - worldY * nextScale,
    });
  }, [updateView]);

  const startPinch = useCallback(() => {
    const session = pointerSession.current;
    const points = Array.from(session.pointers.values());
    if (points.length < 2) return;
    const current = viewRef.current;
    session.pinchStartDistance = pointDistance(points);
    session.pinchStartScale = current.scale;
    session.pinchStartX = current.x;
    session.pinchStartY = current.y;
  }, []);

  const syncSlidesFromIframe = useCallback((node: HTMLIFrameElement | null, targetIndex?: number) => {
    const slides = Array.from(node?.contentDocument?.querySelectorAll<HTMLElement>('.slide') ?? []);
    if (!slides.length) return false;

    const activeIndex = slides.findIndex((slide) => slide.classList.contains('active'));
    const nextIndex = typeof targetIndex === 'number'
      ? ((targetIndex % slides.length) + slides.length) % slides.length
      : activeIndex >= 0
        ? activeIndex
        : 0;

    slides.forEach((slide, i) => slide.classList.toggle('active', i === nextIndex));
    setCount(slides.length);
    setIndex(nextIndex);
    return true;
  }, []);

  const setIframe = useCallback((node: HTMLIFrameElement | null) => {
    setIframeEl(node);
    if (node) window.setTimeout(() => syncSlidesFromIframe(node, 0), 0);
  }, [syncSlidesFromIframe]);

  const callShowSlide = useCallback((targetIndex: number) => {
    if (syncSlidesFromIframe(iframeEl, targetIndex)) return;
    if (!iframeEl?.contentWindow) return;
    const fn = (iframeEl.contentWindow as { showSlide?: (i: number) => void }).showSlide;

    if (typeof fn === 'function') fn(targetIndex);
  }, [iframeEl, syncSlidesFromIframe]);

  const next = useCallback(() => callShowSlide(index + 1), [callShowSlide, index]);
  const prev = useCallback(() => callShowSlide(index - 1), [callShowSlide, index]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button, a, input, textarea, select')) return;
    const session = pointerSession.current;
    session.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture(event.pointerId);

    if (session.pointers.size >= 2) {
      setIsPanning(false);
      startPinch();
      return;
    }

    session.pointerId = event.pointerId;
    session.startX = event.clientX;
    session.startY = event.clientY;
    session.startPanX = viewRef.current.x;
    session.startPanY = viewRef.current.y;
    if (viewRef.current.scale > 1.01) setIsPanning(true);
  }, [startPinch]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const session = pointerSession.current;
    if (!session.pointers.has(event.pointerId)) return;
    session.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const points = Array.from(session.pointers.values());
    if (points.length >= 2 && session.pinchStartDistance > 0) {
      event.preventDefault();
      const rect = bodyRef.current?.getBoundingClientRect();
      if (!rect) return;
      const center = pointCenter(points);
      const localX = center.x - rect.left;
      const localY = center.y - rect.top;
      const nextScale = clampZoom(session.pinchStartScale * (pointDistance(points) / session.pinchStartDistance));
      const worldX = (localX - session.pinchStartX) / session.pinchStartScale;
      const worldY = (localY - session.pinchStartY) / session.pinchStartScale;
      updateView({
        scale: nextScale,
        x: localX - worldX * nextScale,
        y: localY - worldY * nextScale,
      });
      return;
    }

    if (session.pointerId !== event.pointerId || viewRef.current.scale <= 1.01) return;
    event.preventDefault();
    updateView({
      ...viewRef.current,
      x: session.startPanX + event.clientX - session.startX,
      y: session.startPanY + event.clientY - session.startY,
    });
  }, [updateView]);

  const endPointer = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const session = pointerSession.current;
    if (!session.pointers.has(event.pointerId)) return;
    session.pointers.delete(event.pointerId);
    if (session.pointerId === event.pointerId) session.pointerId = undefined;
    setIsPanning(false);

    if (session.pointers.size >= 2) {
      startPinch();
      return;
    }

    const remaining = Array.from(session.pointers.entries())[0];
    if (remaining) {
      session.pointerId = remaining[0];
      session.startX = remaining[1].x;
      session.startY = remaining[1].y;
      session.startPanX = viewRef.current.x;
      session.startPanY = viewRef.current.y;
    }
  }, [startPinch]);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return undefined;

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        zoomBy(event.deltaY < 0 ? 1.1 : 0.9, event.clientX, event.clientY);
        return;
      }

      if (viewRef.current.scale > 1.01) {
        event.preventDefault();
        updateView({
          ...viewRef.current,
          x: viewRef.current.x - event.deltaX,
          y: viewRef.current.y - event.deltaY,
        });
      }
    };

    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => node.removeEventListener('wheel', handleWheel);
  }, [updateView, zoomBy]);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return undefined;

    let active = false;
    let start = {
      scale: 1,
      x: 0,
      y: 0,
      centerX: 0,
      centerY: 0,
    };

    const handleGestureStart = (event: GestureLikeEvent) => {
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      const current = viewRef.current;
      start = {
        scale: current.scale,
        x: current.x,
        y: current.y,
        centerX: (event.clientX ?? rect.left + rect.width / 2) - rect.left,
        centerY: (event.clientY ?? rect.top + rect.height / 2) - rect.top,
      };
      active = true;
    };

    const handleGestureChange = (event: GestureLikeEvent) => {
      if (!active) return;
      event.preventDefault();
      const nextScale = clampZoom(start.scale * (event.scale || 1));
      const worldX = (start.centerX - start.x) / start.scale;
      const worldY = (start.centerY - start.y) / start.scale;
      updateView({
        scale: nextScale,
        x: start.centerX - worldX * nextScale,
        y: start.centerY - worldY * nextScale,
      });
    };

    const handleGestureEnd = (event: Event) => {
      if (!active) return;
      event.preventDefault();
      active = false;
    };

    node.addEventListener('gesturestart', handleGestureStart as EventListener, { passive: false });
    node.addEventListener('gesturechange', handleGestureChange as EventListener, { passive: false });
    node.addEventListener('gestureend', handleGestureEnd as EventListener, { passive: false });

    return () => {
      node.removeEventListener('gesturestart', handleGestureStart as EventListener);
      node.removeEventListener('gesturechange', handleGestureChange as EventListener);
      node.removeEventListener('gestureend', handleGestureEnd as EventListener);
    };
  }, [updateView]);

  useEffect(() => {
    setCount(0);
    setIndex(0);
    resetZoom();
  }, [presentationHtml, resetZoom]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (iframeEl?.contentWindow && e.source !== iframeEl.contentWindow) return;
      if (isReadyEvent(e.data)) setCount(e.data.count);
      else if (isChangedEvent(e.data)) setIndex(e.data.index);
    };

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler);
  }, [iframeEl]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomBy(1.12);
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        zoomBy(0.9);
      } else if (e.key === '0') {
        e.preventDefault();
        resetZoom();
      }
    };

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [onClose, next, prev, resetZoom, zoomBy]);

  if (!presentationHtml) return null;
  const total = Math.max(count, 1);
  const hasMultiple = count > 1;
  const canUsePlanActions = showPlanActions && !!onApprove && !!onDeny;
  const bodyClassName = [
    'dev-plan-explain-modal__body',
    view.scale > 1.01 ? 'is-zoomed' : '',
    isPanning ? 'is-panning' : '',
  ].filter(Boolean).join(' ');

  return createPortal(
    <div
      className="dev-plan-explain-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Plan presentation"
    >
      <div className="dev-plan-explain-modal__panel">
        <header className="dev-plan-explain-modal__header">
          <div className="dev-plan-explain-modal__brand">
            <BrandPendulumMark className="dev-plan-explain-modal__brand-mark" />
            <div className="dev-plan-explain-modal__brand-copy">
              <div className="dev-plan-explain-modal__title">8080.AI Plan Presentation</div>
              <div className="dev-plan-explain-modal__subtitle">Review the implementation plan before approving</div>
            </div>
          </div>
          <div className="dev-plan-explain-modal__meta">
            <div className="dev-plan-explain-modal__zoom" aria-label="Presentation zoom controls">
              <button
                type="button"
                className="dev-plan-explain-modal__control"
                onClick={() => zoomBy(0.9)}
                aria-label="Zoom out presentation"
              >-</button>
              <button
                type="button"
                className="dev-plan-explain-modal__control dev-plan-explain-modal__zoom-value"
                onClick={resetZoom}
                aria-label="Reset presentation zoom"
              >{Math.round(view.scale * 100)}%</button>
              <button
                type="button"
                className="dev-plan-explain-modal__control"
                onClick={() => zoomBy(1.12)}
                aria-label="Zoom in presentation"
              >+</button>
            </div>
            <button
              type="button"
              className="dev-plan-explain-modal__close"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            ><CloseIcon /></button>
          </div>
        </header>
        <div
          ref={bodyRef}
          className={bodyClassName}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
        >
          <iframe
            ref={setIframe}
            title="Plan presentation"
            sandbox="allow-same-origin allow-scripts"
            srcDoc={presentationHtml}
            className="dev-plan-explain-modal__iframe"
            style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
            onLoad={(e) => syncSlidesFromIframe(e.currentTarget, 0)}
          />
        </div>
        <footer className="dev-plan-explain-modal__footer">
          <div className="dev-plan-explain-modal__dots" aria-hidden={!hasMultiple}>
            {Array.from({ length: count }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`dev-plan-explain-modal__dot${i === index ? ' is-active' : ''}`}
                onClick={() => callShowSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
          <div className="dev-plan-explain-modal__plan-group">
            {canUsePlanActions && (
              <div className="dev-plan-explain-modal__plan-actions">
                <button
                  type="button"
                  className="dev-plan-btn dev-plan-btn--approve"
                  onClick={() => onApprove?.()}
                  disabled={!!pending}
                >
                  {pending === 'approve' ? <Spinner /> : <ApproveIcon />}
                  <span>{pending === 'approve' ? 'Approving...' : 'Approve plan'}</span>
                </button>
                <button
                  type="button"
                  className="dev-plan-btn dev-plan-btn--deny"
                  onClick={() => onDeny?.()}
                  disabled={!!pending}
                >
                  {pending === 'deny' ? <Spinner /> : <CloseIcon />}
                  <span>{pending === 'deny' ? 'Denying...' : 'Deny'}</span>
                </button>
              </div>
            )}
            {error && <span className="dev-plan-explain-modal__error" role="alert">{error}</span>}
          </div>
          <div className="dev-plan-explain-modal__controls">
            <button
              type="button"
              className="dev-plan-explain-modal__control"
              onClick={prev}
              disabled={!hasMultiple}
              aria-label="Previous slide"
              title="Previous slide"
            >Prev</button>
            <span className="dev-plan-explain-modal__index">{Math.min(index + 1, total)} / {total}</span>
            <button
              type="button"
              className="dev-plan-explain-modal__control"
              onClick={next}
              disabled={!hasMultiple}
              aria-label="Next slide"
              title="Next slide"
            >Next</button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
};
